//src/pages/cadastros/RiskFormPage.tsx
import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Check,
  Link2,
  Loader2,
  Save,
  Search,
  Unlink2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  useAvailableActionPlanModels,
  useCreateRisk,
  useLinkActionPlanModelToRisk,
  useRiskActionPlanModels,
  useRiskCategories,
  useRiskDetail,
  useUnlinkActionPlanModelFromRisk,
  useUpdateRisk,
} from "@/hooks/useRisks";
import type { RiskCatalogCreateIn } from "@/types/risks";

const EMPTY_FORM: RiskCatalogCreateIn = {
  risk_category_id: "",
  code: "",
  name: "",
  technical_description: "",
  legal_reference: "",
  possible_damage: "",
  example_situations: "",
  control_guidelines: "",
  is_active: true,
};

function normalizeCode(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.slice(0, 4))
    .join("_")
    .slice(0, 40);
}

function buildVisiblePages(current: number, total: number) {
  const pages = new Set<number>();
  pages.add(1);
  pages.add(total);
  pages.add(current);
  if (current - 1 >= 1) pages.add(current - 1);
  if (current + 1 <= total) pages.add(current + 1);

  return Array.from(pages)
    .filter((page) => page >= 1 && page <= total)
    .sort((a, b) => a - b);
}

export default function RiskFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCreating = !id;

  const detailQ = useRiskDetail(id);
  const linkedPlansQ = useRiskActionPlanModels(id);
  const categoriesQ = useRiskCategories({ is_active: true });
  const createMut = useCreateRisk();
  const updateMut = useUpdateRisk();
  const linkMut = useLinkActionPlanModelToRisk();
  const unlinkMut = useUnlinkActionPlanModelFromRisk();

  const [form, setForm] = useState<RiskCatalogCreateIn>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<"cadastro" | "planos">("cadastro");
  const [codeTouched, setCodeTouched] = useState(false);

  const [searchPlan, setSearchPlan] = useState("");
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);
  const [linkingMany, setLinkingMany] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<10 | 20 | 30>(10);

  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<{
    actionPlanModelId: string;
    title: string;
  } | null>(null);

  const availablePlansQ = useAvailableActionPlanModels(id, { is_active: true });

  useEffect(() => {
    if (!detailQ.data) return;
    setForm({
      risk_category_id: detailQ.data.risk_category_id,
      code: detailQ.data.code,
      name: detailQ.data.name,
      technical_description: detailQ.data.technical_description || "",
      legal_reference: detailQ.data.legal_reference || "",
      possible_damage: detailQ.data.possible_damage || "",
      example_situations: detailQ.data.example_situations || "",
      control_guidelines: detailQ.data.control_guidelines || "",
      is_active: detailQ.data.is_active,
    });
    setCodeTouched(true);
  }, [detailQ.data]);

  useEffect(() => {
    setPage(1);
  }, [searchPlan, pageSize, id]);

  const categories = categoriesQ.data ?? [];
  const linkedPlans = linkedPlansQ.data ?? [];
  const availablePlans = availablePlansQ.data ?? [];

  const linkedPlanIds = useMemo(
    () => new Set(linkedPlans.map((plan) => plan.action_plan_model_id || plan.id)),
    [linkedPlans],
  );

  const selectedCategoryTheme = useMemo(() => {
    const category = categories.find((item) => item.id === form.risk_category_id);
    return category?.risk_theme ?? "";
  }, [categories, form.risk_category_id]);

  const filteredAvailablePlans = useMemo(() => {
    const term = searchPlan.trim().toLowerCase();

    return availablePlans.filter((plan) => {
      if (!plan.is_active) return false;
      if (linkedPlanIds.has(plan.id)) return false;

      if (!term) return true;

      const title = (plan.title || "").toLowerCase();
      const planId = (plan.id || "").toLowerCase();

      return title.includes(term) || planId.includes(term);
    });
  }, [availablePlans, searchPlan, linkedPlanIds]);

  const totalPages = Math.max(1, Math.ceil(filteredAvailablePlans.length / pageSize));

  const paginatedAvailablePlans = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;
    return filteredAvailablePlans.slice(start, start + pageSize);
  }, [filteredAvailablePlans, page, pageSize, totalPages]);

  const visiblePages = useMemo(
    () => buildVisiblePages(Math.min(page, totalPages), totalPages),
    [page, totalPages],
  );

  const pageStart = filteredAvailablePlans.length === 0 ? 0 : (Math.min(page, totalPages) - 1) * pageSize + 1;
  const pageEnd = Math.min(Math.min(page, totalPages) * pageSize, filteredAvailablePlans.length);

  const allVisibleSelected =
    paginatedAvailablePlans.length > 0 &&
    paginatedAvailablePlans.every((plan) => selectedPlanIds.includes(plan.id));

  function updateField<K extends keyof RiskCatalogCreateIn>(
    field: K,
    value: RiskCatalogCreateIn[K],
  ) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleNameChange(value: string) {
    setForm((prev) => ({
      ...prev,
      name: value,
      code: codeTouched ? prev.code : normalizeCode(value),
    }));
  }

  function togglePlanSelection(planId: string) {
    setSelectedPlanIds((prev) =>
      prev.includes(planId) ? prev.filter((id) => id !== planId) : [...prev, planId],
    );
  }

  function toggleSelectAllVisible(checked: boolean) {
    const visibleIds = paginatedAvailablePlans.map((plan) => plan.id);

    if (checked) {
      setSelectedPlanIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
      return;
    }

    setSelectedPlanIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
  }

  async function handleSave() {
    if (!form.risk_category_id) {
      toast.error("Selecione a categoria do risco.");
      return;
    }
    if (!form.name.trim()) {
      toast.error("Informe o nome do risco.");
      return;
    }
    if (!form.code.trim()) {
      toast.error("Informe o código do risco.");
      return;
    }

    setSaving(true);
    try {
      if (id) {
        await updateMut.mutateAsync({ riskId: id, body: form });
        toast.success("Risco atualizado com sucesso.");
      } else {
        const created = await createMut.mutateAsync(form);
        toast.success("Risco criado com sucesso.");
        navigate(`/cadastros/riscos/${created.id}`);
        return;
      }
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar risco.");
    } finally {
      setSaving(false);
    }
  }

  async function handleLinkSelectedPlans() {
    if (!id || !selectedPlanIds.length) {
      toast.error("Selecione ao menos um plano para vincular.");
      return;
    }

    setLinkingMany(true);
    try {
      for (const planId of selectedPlanIds) {
        await linkMut.mutateAsync({
          riskId: id,
          body: { action_plan_model_id: planId },
        });
      }

      toast.success(
        selectedPlanIds.length === 1
          ? "Plano vinculado ao risco com sucesso."
          : `${selectedPlanIds.length} planos vinculados ao risco com sucesso.`,
      );

      setSelectedPlanIds([]);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao vincular planos ao risco.");
    } finally {
      setLinkingMany(false);
    }
  }

  function openUnlinkDialog(actionPlanModelId: string, title: string) {
    setUnlinkTarget({ actionPlanModelId, title });
    setUnlinkDialogOpen(true);
  }

  async function handleConfirmUnlink() {
    if (!id || !unlinkTarget) return;

    try {
      await unlinkMut.mutateAsync({
        riskId: id,
        actionPlanModelId: unlinkTarget.actionPlanModelId,
      });
      toast.success("Plano desvinculado do risco com sucesso.");
      setUnlinkDialogOpen(false);
      setUnlinkTarget(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao desvincular plano do risco.");
    }
  }

  if (!isCreating && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="pb-10">
      <PageHeader
        title={isCreating ? "Novo Risco" : "Editar Risco"}
        description="Cadastro do risco e gestão dos planos de ação vinculados."
      >
        <Button variant="outline" onClick={() => navigate("/cadastros/riscos")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Salvar
        </Button>
      </PageHeader>

      <Tabs value={tab} onValueChange={(value) => setTab(value as typeof tab)}>
        <TabsList className="mb-6 h-auto gap-2 rounded-xl bg-muted/60 p-1">
          <TabsTrigger value="cadastro" className="rounded-lg px-4 py-2">
            Cadastro do Risco
          </TabsTrigger>
          <TabsTrigger
            value="planos"
            disabled={isCreating}
            className="rounded-lg px-4 py-2"
          >
            Planos de Ação {!!linkedPlans.length && `(${linkedPlans.length})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cadastro">
          <Card className="rounded-2xl shadow-sm">
            <CardHeader>
              <CardTitle>Dados do risco</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Nome *</Label>
                <Input
                  className="mt-1"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Ex.: Estresse ocupacional"
                />
              </div>

              <div>
                <Label>Código *</Label>
                <Input
                  className="mt-1"
                  value={form.code}
                  onChange={(e) => {
                    setCodeTouched(true);
                    updateField("code", normalizeCode(e.target.value));
                  }}
                  placeholder="Ex.: ESTR_OCC"
                />
              </div>

              <div>
                <Label>Categoria *</Label>
                <select
                  className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  value={form.risk_category_id}
                  onChange={(e) => updateField("risk_category_id", e.target.value)}
                >
                  <option value="">Selecione</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Tema</Label>
                <Input className="mt-1" value={selectedCategoryTheme || ""} disabled />
              </div>

              <div className="md:col-span-2">
                <Label>Descrição técnica</Label>
                <Textarea
                  className="mt-1 min-h-[100px]"
                  value={form.technical_description ?? ""}
                  onChange={(e) => updateField("technical_description", e.target.value)}
                />
              </div>

              <div className="md:col-span-2">
                <Label>Referência legal</Label>
                <Textarea
                  className="mt-1 min-h-[100px]"
                  value={form.legal_reference ?? ""}
                  onChange={(e) => updateField("legal_reference", e.target.value)}
                />
              </div>

              <div className="md:col-span-2">
                <Label>Possíveis danos</Label>
                <Textarea
                  className="mt-1 min-h-[100px]"
                  value={form.possible_damage ?? ""}
                  onChange={(e) => updateField("possible_damage", e.target.value)}
                />
              </div>

              <div className="md:col-span-2">
                <Label>Situações de exemplo</Label>
                <Textarea
                  className="mt-1 min-h-[100px]"
                  value={form.example_situations ?? ""}
                  onChange={(e) => updateField("example_situations", e.target.value)}
                />
              </div>

              <div className="md:col-span-2">
                <Label>Diretrizes de controle</Label>
                <Textarea
                  className="mt-1 min-h-[100px]"
                  value={form.control_guidelines ?? ""}
                  onChange={(e) => updateField("control_guidelines", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="planos">
          <div className="space-y-6">
            <Card className="rounded-2xl shadow-sm">
              <CardHeader>
                <CardTitle>Adicionar vínculos</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Busque por <span className="font-medium">título</span> ou{" "}
                  <span className="font-medium">ID</span>, selecione um ou mais planos
                  ativos ainda não vinculados e confirme o vínculo.
                </p>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="relative w-full md:max-w-md">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    value={searchPlan}
                    onChange={(e) => setSearchPlan(e.target.value)}
                    placeholder="Buscar por título ou ID"
                  />
                </div>

                <div className="rounded-xl border">
                  <div className="grid grid-cols-[44px_160px_1fr] gap-3 border-b bg-muted/40 px-4 py-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                        className="h-4 w-4 rounded border-border"
                        aria-label="Selecionar todos os planos exibidos"
                      />
                    </div>
                    <div>Código</div>
                    <div>Plano de Ação</div>
                  </div>

                  <div className="max-h-[480px] overflow-y-auto">
                    {availablePlansQ.isLoading ? (
                      <div className="flex items-center justify-center py-10">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : !paginatedAvailablePlans.length ? (
                      <div className="p-6 text-center text-sm text-muted-foreground">
                        Nenhum plano ativo disponível para vincular.
                      </div>
                    ) : (
                      paginatedAvailablePlans.map((plan) => {
                        const isSelected = selectedPlanIds.includes(plan.id);

                        return (
                          <button
                            key={plan.id}
                            type="button"
                            onClick={() => togglePlanSelection(plan.id)}
                            className={`grid w-full grid-cols-[44px_160px_1fr] gap-3 border-b px-4 py-3 text-left transition last:border-b-0 hover:bg-muted/40 ${
                              isSelected ? "bg-primary/5" : "bg-background"
                            }`}
                          >
                            <div className="flex items-center justify-center">
                              <span
                                className={`flex h-5 w-5 items-center justify-center rounded border ${
                                  isSelected
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : "border-border bg-background"
                                }`}
                              >
                                {isSelected ? <Check className="h-3.5 w-3.5" /> : null}
                              </span>
                            </div>

                            <div className="text-sm font-medium">{plan.code}</div>

                            <div className="min-w-0">
                              <div className="text-sm font-medium">{plan.title}</div>
                              <div className="mt-1 truncate text-xs text-muted-foreground">
                                {plan.description || `${plan.items_count} ações cadastradas`}
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-3 rounded-xl border bg-background px-4 py-3 md:flex-row md:items-center md:justify-between">
                  <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                    <span>
                      Exibindo {pageStart} a {pageEnd} de {filteredAvailablePlans.length} resultados
                    </span>

                    <span>Por página</span>

                    <select
                      className="h-10 rounded-md border bg-background px-3 text-sm"
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value) as 10 | 20 | 30)}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={30}>30</option>
                    </select>

                    <Button
                      onClick={handleLinkSelectedPlans}
                      disabled={!selectedPlanIds.length || linkingMany}
                    >
                      {linkingMany ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Link2 className="mr-2 h-4 w-4" />
                      )}
                      Vincular selecionados
                    </Button>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Página {Math.min(page, totalPages)} de {totalPages}
                    </span>

                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      disabled={page <= 1}
                    >
                      <span className="sr-only">Página anterior</span>
                      ‹
                    </Button>

                    {visiblePages.map((pageNumber) => (
                      <Button
                        key={pageNumber}
                        variant={pageNumber === Math.min(page, totalPages) ? "default" : "outline"}
                        className="min-w-10"
                        onClick={() => setPage(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    ))}

                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={page >= totalPages}
                    >
                      <span className="sr-only">Próxima página</span>
                      ›
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-sm">
              <CardHeader>
                <CardTitle>Planos vinculados ao risco</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Gerencie apenas os vínculos de planos com este risco.
                </p>
              </CardHeader>

              <CardContent>
                {linkedPlansQ.isLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : !linkedPlans.length ? (
                  <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
                    Nenhum plano de ação vinculado a este risco.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {linkedPlans.map((plan) => (
                      <div
                        key={plan.id}
                        className="flex flex-col gap-3 rounded-xl border p-4 md:flex-row md:items-center md:justify-between"
                      >
                        <div className="min-w-0">
                          <div className="font-medium">{plan.title}</div>
                          <div className="mt-1 text-sm text-muted-foreground">
                            Código: {plan.code} • {plan.items_count} ações cadastradas
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              openUnlinkDialog(plan.action_plan_model_id, plan.title)
                            }
                            disabled={unlinkMut.isPending}
                          >
                            <Unlink2 className="mr-2 h-4 w-4" />
                            Desvincular
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <ConfirmDialog
            open={unlinkDialogOpen}
            onOpenChange={setUnlinkDialogOpen}
            title="Desvincular plano de ação"
            description={`Tem certeza que deseja desvincular o plano "${unlinkTarget?.title || ""}" deste risco?`}
            confirmLabel="Desvincular"
            onConfirm={handleConfirmUnlink}
            variant="destructive"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
